<template>
  <GmapMap
    style="height: 100%"
    ref="map"
    :center="DEFAULT_CENTER"
    :zoom="zoom"
    @zoom_changed="onZoomChanged"
    :options="{
      zoomControl: true,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google && google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        position: google && google.maps.ControlPosition.TOP_RIGHT
      },
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      disableDefaultUi: false,
      gestureHandling: 'greedy'
    }"
  >
    <GmapCluster
      :zoomOnClick="true"
      :averageCenter="true"
      :minimumClusterSize="DEFAULT_MIN_CLUSTER_SIZE"
      :ignoreHidden="true"
      :imagePath="getPublicImgPath('img/maps/cluster/m')"
    >
      <GmapMarker
        :key="index"
        v-for="(m, index) in tasksMarkers"
        :position="m.position"
        :animation="m.animation"
        :clickable="true"
        :draggable="false"
        :visible="m.visible"
        :icon="getPublicImgPath('img/maps/icon-task-' + m.status + '.png')"
        @click="toggleInfoWindow(m, index)"
      />
    </GmapCluster>

    <!-- MARKERS WITH ICONS -->
    <template v-if="!showPathCount">
      <GmapMarker
        v-for="(m, index) in pathMarkers"
        :key="index"
        :position="m.position"
        :clickable="true"
        :draggable="false"
        :visible="path"
        :label="getPathMarkerLabel(index)"
        :icon="getPathMarkerIcon(m, index)"
        @mouseover="toggleInfoWindowPathMarker(m, index)"
        @mouseout="toggleInfoWindowPathMarker(m, index)"
      />

      <!-- MAIN PATH -->
      <GmapPolyline
        v-if="pathLines.length"
        :path="pathLines"
        :options="{
          strokeColor: '#3498db',
          strokeOpacity: path ? 1 : 0,
          strokeWeight: 2,
          zIndex: 1
        }"
      />

      <!-- NO GPS PATH -->
      <GmapPolyline
        v-for="(item, index) in pathsWithNoGps"
        :key="index + Math.random()"
        :path="item"
        :options="{
          strokeColor: '#d3b018',
          strokeOpacity: path ? 1 : 0,
          strokeWeight: 2,
          zIndex: 2
        }"
      />

      <!-- SNAPPED PATH -->
      <GmapPolyline
        v-if="pathLinesSnapped.length"
        :path="pathLinesSnapped"
        :options="{
          strokeColor: '#fe6262',
          strokeOpacity: path && snapped ? 1 : 0,
          strokeWeight: 3,
          zIndex: 3
        }"
      />
    </template>

    <!-- KilOMETER COUNT -->
    <template v-else>
        <GmapPolyline
          v-for="(line, index) in multiPathsLines"
          :key="`${index}-polyline-${Math.random()}`"
          :path="line"
          :options="{
            strokeColor: getStrokeColor(index),
            strokeOpacity: path ? 1  : 0,
            strokeWeight: 2,
            zIndex: 2
          }"
        />
        <GmapMarker
          v-for="(marker, index) in pathCountMarkers"
          :key="marker.timestamp"
          :position="marker.position"
          :clickable="false"
          :draggable="false"
          :visible="path"
          :label="getPathCountMarkerLabel(index)"
          :icon="getPathCountMarkerIcon(index)"
        />
    </template>

    <GmapInfoWindow
      :options="infoOptions"
      :position="infoWindowPos"
      :opened="infoWinOpen"
      @closeclick="closeInfoWindow"
    >
      <div v-html="infoContent"></div>
    </GmapInfoWindow>
  </GmapMap>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { gmapApi } from 'vue2-google-maps'
import { MarkerPosition, PathMarker, TaskMarker, SnapToRoadResponse, MarkerLabel, MarkerIcon, Point } from '@/types//Maps'
import { TrackingUser, TrackingUserActivityType, TrackingUserPosition, TrackingUserTask, TrackingUserTaskLocationStatus } from '@/types/Tracking'
import moment from 'moment'
import trackingService from '@/services/TrackingService'
import googleMapsService from '@/services/GoogleMapsService'
import mapboxService from '@/services/MapboxService'
import config from '@/config'

/**
 * Component that shows the user tasks positions and the path folowed by the user
 * during a specific day
 */
@Component
export default class TasksUsersLocationMap extends Vue {
  /**
   * User tasks
   */
  @Prop({ required: true })
  tasks: TrackingUserTask[]

  /**
   * Tasks status to be shown
   */
  @Prop({ required: false, default: [] })
  status: number[]

  /**
   * Current user
   */
  @Prop({ required: true })
  user: TrackingUser

  /**
   * Selected task
   */
  @Prop({ required: false })
  selected: any

  /**
   * Priority: "tasks" or "path".
   * The priority determines which markers will be fitted (tasks markers or path markers)
   */
  @Prop({ required: true })
  priority: string

  /**
   * Current user
   */
  @Prop({ required: true })
  path: boolean

  /**
   * Show snapped path
   */
  @Prop({ required: false, default: false })
  snapped: boolean

  // Constants
  DEFAULT_CENTER = new MarkerPosition(25, -108)
  DEFAULT_ZOOM = 6
  DEFAULT_MIN_CLUSTER_SIZE = 5
  STROKE_COLORS = ['#34668e', '#188e00', '#82348e', '#ad1214', '#E89103']

  // Markers
  tasksMarkers: TaskMarker[] = []
  pathMarkers: PathMarker[] = []
  currentMarker?: any
  zoom = this.DEFAULT_ZOOM

  // Path lines
  pathLines: MarkerPosition[] = []
  pathLinesSnapped: MarkerPosition[] = []
  pathsWithNoGps: MarkerPosition[][] = []

  // Multiple Paths for path count
  showPathCount = false
  pathCountMarkers:PathMarker[] = []
  multiPathsLines: Array<Array<MarkerPosition>> = []
  pathLineEstimate: boolean[] = []
  pathsDistancesData: any[] = []

  // Data updates
  intervalToSnapPath: any = ''
  lastSnappedIndex = 0
  pathCentered = false
  lastPositionAdded = ''
  gpsOn = true

  // Task marker window
  infoContent = ''
  infoWindowPos = new MarkerPosition(0, 0)
  infoWinOpen = false
  currentMidx = null
  infoOptions = {
    pixelOffset: {
      width: 0,
      height: -35
    }
  }

  mounted () {
    this.$root.$on('EVENT_TRACKING_ADD_USER_PATH', this.addUserPath)
    this.$root.$on('EVENT_TRACKING_TASKS_RESET', this.resetTasks)
    this.$root.$on('EVENT_TRACKING_USER_PATH_RESET', this.resetPath)
    this.$root.$on('EVENT_TRACKING_TOGGLE_PATH_COUNT', this.setPathCount)
  }

  beforeDestroy () {
    this.$root.$off('EVENT_TRACKING_ADD_USER_PATH', this.addUserPath)
    this.$root.$off('EVENT_TRACKING_TASKS_RESET', this.resetTasks)
    this.$root.$off('EVENT_TRACKING_USER_PATH_RESET', this.resetPath)
    this.$root.$off('EVENT_TRACKING_TOGGLE_PATH_COUNT', this.setPathCount)
    this.clearIntervalToSnapPath()
  }

  get google () {
    return gmapApi()
  }

  get map (): any {
    return this.$refs.map
  }

  onZoomChanged (zoom: number) {
    this.zoom = zoom
  }

  /**
   * When tasks list changes, the map is initialized
   */
  @Watch('tasks')
  watchTasks () {
    this.initializeTasksMap()
  }

  /**
   * Fit the map to path if there are no tasks markers
   */
  @Watch('tasksMarkers')
  watchTasksMarkers (markers: any[]) {
    if (!markers.length && this.path) {
      this.fitUserPath()
    }
  }

  /**
   * When the visible status change, the tasks are filtered
   */
  @Watch('status')
  watchStatus () {
    this.filterTasksMarkers()
  }

  /**
   * Fit the map to tasks if there is no path
   */
  @Watch('path')
  watchPathFlag (path: boolean) {
    if (path) {
      if (this.pathLines.length) this.fitUserPath()
    } else {
      this.infoWinOpen = false
      if (this.tasksMarkers.length) {
        this.filterTasksMarkers()
      }
    }
  }

  /**
   * When the selected task changes, update the animation and info window status
   */
  @Watch('selected')
  watchTaskSelected (task: any) {
    if (this.currentMarker) {
      this.currentMarker.animation = 0
    }

    if (task) {
      this.currentMarker = this.tasksMarkers.find(it => it.id === task.id)
      if (this.currentMarker) {
        this.currentMarker.animation = this.google.maps.Animation.BOUNCE
      }
    } else {
      this.closeInfoWindow()
    }
  }

  /**
   * When the current user changes, the path ans snapped path must be cleaned
   */
  @Watch('user')
  watchUser () {
    this.resetPath()
  }

  /**
   * Start an interval that updates the snapped path lines based on the position updates
   */
  initIntervalToSnapPath () {
    this.clearIntervalToSnapPath()
    this.intervalToSnapPath = setInterval(() => {
      this.traceSnapPath()
    }, 500)
  }

  /**
   * Stop and destroy the current interval
   */
  clearIntervalToSnapPath () {
    if (this.intervalToSnapPath) {
      clearInterval(this.intervalToSnapPath)
    }
  }

  /**
   * Trace the snappped paths
   */
  traceSnapPath () {
    if (!this.snapped) return

    let pathsToStap = this.pathLines.slice(this.lastSnappedIndex, this.lastSnappedIndex + config.tracking.snapToRoadsPageRequestSize)
    if (pathsToStap.length) {
      this.lastSnappedIndex += pathsToStap.length
      let locations = pathsToStap.map(it => {
        return it.lat + ',' + it.lng
      }, [])

      googleMapsService.getSnapToRoadsLocations(locations).then((data: SnapToRoadResponse) => {
        let lines = []
        for (let s of data.snappedPoints) {
          const newLocation = new MarkerPosition(s.location.latitude, s.location.longitude)
          lines.push(newLocation)
        }
        this.pathLinesSnapped = this.pathLinesSnapped.concat(lines)
      })
    }
  }

  count = 0

  /**
   * Adds a new position based on the user updates
   */
  addUserPath (position: TrackingUserPosition) {
    if (position.position) {
      const markerPosition = MarkerPosition.fromString(position.position)

      if (position.position !== this.lastPositionAdded) {
        this.lastPositionAdded = position.position

        let path = new PathMarker()
        path.position = markerPosition
        path.time = position.time
        path.timestamp = position._timestamp.valueOf()
        path.device = position.device
        path.mock = position.mock
        path.heading = position.heading
        path.activityType = position.activityType

        this.pathMarkers.push(path)
        this.pathLines.push(markerPosition)

        if (this.pathsDistancesData.length > 0) {
          this.addPathCount(path)
        }

        // Center map. Wait 2 seconds until the list is filled with several items
        if (!this.pathCentered && this.priority === 'path' && this.pathMarkers.length) {
          this.pathCentered = true
          setTimeout(() => {
            this.fitUserPath()
          }, 2000)
        }
      }

      if (position.event === 'providerchange') {
        let path = new PathMarker()
        path.position = markerPosition
        path.time = position.time
        path.timestamp = position._timestamp.valueOf()
        path.device = position.device

        if (position._gpsOn && !this.gpsOn) {
          // GPS is enabled again
          this.gpsOn = true
          this.pathsWithNoGps[this.pathsWithNoGps.length - 1].push(markerPosition)
          path.gpsOn = true
          this.pathMarkers.push(path)
        } else if (!position._gpsOn && this.gpsOn) {
          // GPS is disabled
          this.gpsOn = false
          this.pathsWithNoGps.push([markerPosition])
          path.gpsOff = true
          this.pathMarkers.push(path)
        }

        if (this.gpsOn && !position._gpsOn) {
          this.pathsWithNoGps.push([MarkerPosition.fromString(position.position)])
        }
      }
    }
  }

  /**
   * Adjusts the zoom in order to fit all markers
   */
  fitUserPath () {
    let bounds = new this.google.maps.LatLngBounds()
    let count = 0
    this.pathMarkers.forEach(it => {
      bounds.extend(it.position)
      count++
    })
    if (count) this.map.fitBounds(bounds)
  }

  /**
   * Get distances between positions, set pathLines, and get kilometer count
   */
  async setPathCount (active: boolean) {
    if (active) {
      this.showPathCount = false
      return
    }

    this.showPathCount = true

    if (this.pathsDistancesData.length === 0) {
      let distances = []
      let timeDifferences = []
      const n = this.pathLines.length - 1

      for (let i = 0; i < n; i++) {
        const pathLine1 = this.pathLines[i]
        const pathLine2 = this.pathLines[i + 1]
        const pathMarker1 = this.pathMarkers[i]
        const pathMarker2 = this.pathMarkers[i + 1]
        const distance = pathLine1.haversineDistance(pathLine2) * 1000 // to meters
        distances.push(distance)
        timeDifferences.push((pathMarker2.timestamp - pathMarker1.timestamp) / 1000)
      }

      const meanDistance = this.calculateMean(distances)
      const stdVarDistance = this.calculateStdVar(distances)
      const meanTimeDiff = this.calculateMean(timeDifferences)
      const stdVarTimeDiff = this.calculateStdVar(timeDifferences)
      const toleranceDistance = meanDistance + stdVarDistance
      const toleranceTime = meanTimeDiff + stdVarTimeDiff

      let pathsLines: Array<Array<MarkerPosition>> = []
      let pathMarkers: PathMarker[] = [this.pathMarkers[0]]
      let pathLines: MarkerPosition[] = []
      let pathLineEstimate: boolean[] = []
      let pathsDistances: number[] = []
      let pathDistance = 0
      for (let i = 0; i < n - 1; i++) {
        pathLines.push(this.pathLines[i])
        // Distance between 2 points is too far apart
        if (distances[i] > toleranceDistance && timeDifferences[i] > toleranceTime && distances[i] > 250) {
          // Approximate path distance using mapbox
          const origin = `${this.pathLines[i].lng},${this.pathLines[i].lat}`
          const destination = `${this.pathLines[i + 1].lng},${this.pathLines[i + 1].lat}`
          const response = await mapboxService.getDirections(origin, destination)
          const route = response.routes[0]
          const estimatedLines = route.geometry.coordinates.map((coord: number[]) => {
            const [long, lat] = coord
            return new MarkerPosition(lat, long)
          })
          let estimatedMarker = new PathMarker(estimatedLines[0].lat, estimatedLines[0].lng)
          estimatedMarker.timestamp = Date.now()

          pathMarkers.push(estimatedMarker)
          pathMarkers.push(this.pathMarkers[i + 1])
          pathsLines.push(pathLines)
          pathLineEstimate.push(false)
          pathsLines.push(estimatedLines)
          pathLineEstimate.push(true)
          pathLines = []
          pathsDistances.push(pathDistance)
          pathsDistances.push(route.distance)
          pathDistance = 0
        } else {
          pathDistance += distances[i]
        }
      }
      pathMarkers.push(this.pathMarkers[n])
      pathLines.push(this.pathLines[n])
      pathsLines.push(pathLines)
      pathLineEstimate.push(false)
      pathsDistances.push(pathDistance)

      this.pathCountMarkers = pathMarkers
      this.multiPathsLines = pathsLines
      this.pathLineEstimate = pathLineEstimate

      this.pathsDistancesData = pathsDistances.map((num, index) => {
        return {
          value: num,
          color: this.STROKE_COLORS[index % this.STROKE_COLORS.length],
          isEstimate: this.pathLineEstimate[index]
        }
      })
    }

    this.$root.$emit('EVENT_TRACKING_SET_PATH_COUNT', this.pathsDistancesData)
  }

  addPathCount (path: PathMarker) {
    const numLines = this.multiPathsLines.length
    let newLine = new MarkerPosition(path.position.lat, path.position.lng)
    const lastPath = this.multiPathsLines[numLines - 1]
    const lastPosition = lastPath[lastPath.length - 1]
    this.multiPathsLines[numLines - 1].push(newLine)
    this.pathCountMarkers.pop()
    this.pathCountMarkers.push(path)
    this.pathsDistancesData[numLines - 1].value += lastPosition.haversineDistance(newLine) * 1000
    this.$root.$emit('EVENT_TRACKING_SET_PATH_COUNT', this.pathsDistancesData)
  }

  /**
   * Reset the path markers
   */
  resetPath () {
    this.pathMarkers = []
    this.pathLines = []
    this.pathsWithNoGps = []
    this.gpsOn = true
    this.pathCentered = false
    this.resetSnappedPath()
    this.count = 0
    this.multiPathsLines = []
    this.pathCountMarkers = []
    this.pathLineEstimate = []
    this.pathsDistancesData = []
    this.showPathCount = false
  }

  /**
   * Reset the path markers
   */
  resetSnappedPath () {
    this.pathLinesSnapped = []
    this.lastPositionAdded = ''
    this.lastSnappedIndex = 0
    this.initIntervalToSnapPath()
  }

  /**
   * Reset the tasks markers
   */
  resetTasks () {
    this.tasksMarkers = []
  }

  /**
   * Init the user tasks markers
   */
  initializeTasksMap () {
    const validTasks = trackingService.filterValidTasks(this.tasks)

    this.tasksMarkers = validTasks.map((it: TrackingUserTask) => {
      let marker = new TaskMarker()

      if (it.status === TrackingUserTaskLocationStatus.UNCOMPLETE) {
        marker.position = new MarkerPosition(it.fillLocation.latitude, it.fillLocation.longitude, it.fillLocation.altitude)
      } else {
        marker.position = new MarkerPosition(it.visitedLocation.latitude, it.visitedLocation.longitude, it.visitedLocation.altitude)
      }

      marker.id = it.id
      marker.name = it.fullIdent
      marker.status = it.status
      marker.receivedDate = it.receivedDate ? moment(this.toUTCZuluTimestamp(it.receivedDate)).format('MMMM DD, YYYY - HH:mm') : '--'
      return marker
    })

    this.filterTasksMarkers()
  }

  /**
   * Auxiliar function to validate all processed timestamps are as Zulu time,
   * refering UTC timestamps
   * @param {string} timestamp  - ISO timestamp to validate
   * @returns {string}          - ISO timestamp with Zulu character
   */
  private toUTCZuluTimestamp (date: number): string {
    let timestamp = date.toString()
    return timestamp.endsWith('Z') ? timestamp : timestamp + 'Z'
  }

  /**
   * Filter the tasks markers based on the task status to be shown
   */
  filterTasksMarkers () {
    this.infoWinOpen = false
    let bounds = new this.google.maps.LatLngBounds()
    let count = 0
    this.tasksMarkers.forEach(it => {
      it.visible = this.status.includes(it.status)
      if (it.visible) {
        count++
        bounds.extend(it.position)
      }
    })
    if (this.priority === 'tasks' && count) {
      setTimeout(() => {
        if (count > 1) {
          this.map.fitBounds(bounds)
        } else {
          this.zoom = 17
          this.map.panTo(bounds.getCenter())
        }
      }, 500)
    }
  }

  /**
   * Show / Hide info window from the selected task's marker
   */
  toggleInfoWindow (marker: any, idx: any) {
    this.infoWindowPos = marker.position
    this.infoContent = this.getInfoWindowContent(marker)

    if (this.currentMidx === idx) {
      // check if its the same marker that was selected if yes toggle
      this.infoWinOpen = !this.infoWinOpen
      this.$emit('closeInfo')
    } else {
      // if different marker set infowindow to open and reset current marker index
      this.infoWinOpen = true
      this.currentMidx = idx
      this.$emit('select', marker.id)
    }
  }

  /**
   * Close the active info window
   */
  closeInfoWindow () {
    this.infoWinOpen = false
    this.$emit('closeInfo')
  }

  /**
   * Get the HTML content of the info window
   */
  getInfoWindowContent (marker: TaskMarker) {
    return `
      <div class="bm-gm--iw">
        <h1>${marker.name}</h1>
        <h2>Fecha de envío: ${marker.receivedDate}</h2>
      </div>
    `
  }
  /**
   * puts the icon according to the type of movement or stillness
   * @param action of type string
   */
  putIconAccordingToAction (action: TrackingUserActivityType) {
    if (action === TrackingUserActivityType.STILL) {
      return '<i class="fas fa-male"></i>'
    } else if (action === TrackingUserActivityType.WALKING || action === TrackingUserActivityType.ON_FOOT) {
      return '<i class="fas fa-walking"></i>'
    } else if (action === TrackingUserActivityType.RUNNING) {
      return '<i class="fas fa-running"></i>'
    } else if (action === TrackingUserActivityType.ON_BICYCLE) {
      return '<i class="fa-biking"></i>'
    } else if (action === TrackingUserActivityType.IN_VEHICLE) {
      return '<i class="fas fa-car"></i>'
    } else {
      return '<i class="fas fa-male"></i>'
    }
  }

  /**
   * Get the HTML content of the info window for path markers
   */
  getInfoWindowContentPathMarker (marker: PathMarker) {
    let grades = 0
    let content = '<div class="bm-gm--iw">'

    if (marker.heading >= 0) {
      if (marker.heading >= 20 && marker.heading < 65) {
        grades = 45
      } else if (marker.heading >= 65 && marker.heading < 110) {
        grades = 90
      } else if (marker.heading >= 110 && marker.heading < 155) {
        grades = 135
      } else if (marker.heading >= 155 && marker.heading < 200) {
        grades = 180
      } else if (marker.heading >= 200 && marker.heading < 245) {
        grades = 225
      } else if (marker.heading >= 245 && marker.heading < 290) {
        grades = 270
      } else if (marker.heading >= 290 && marker.heading < 340) {
        grades = 315
      }
    }

    content += `
      <h1>
        <img src="${this.getPublicImgPath('img/maps/path-point-' + grades + '.png')}">
        | Hora: ${marker.time} |
        <span>
          ${this.putIconAccordingToAction(marker.activityType)}
        </span>
      </h1>
      <h2>
        Dispositivo: ${marker.device}
      </h2>`

    if (marker.mock) {
      content += '<h2>** MOCK GPS **</h2>'
    }

    if (marker.gpsOn) {
      content += '<h2>GPS fue activado</h2>'
    }

    if (marker.gpsOff) {
      content += '<h2>GPS fue apagado</h2>'
    }

    return content + '</div>'
  }

  getPathMarkerIcon (marker: PathMarker, index: number) {
    if (index === 0) {
      let icon = new MarkerIcon()
      icon.url = this.getPublicImgPath('img/maps/path-point-start.png')
      icon.labelOrigin = new Point(60, 8)
      return icon
    }

    if (index === this.pathMarkers.length - 1) {
      let icon = new MarkerIcon()
      icon.url = this.getPublicImgPath('img/maps/path-point-end.png')
      icon.labelOrigin = new Point(60, 10)
      return icon
    }

    if (marker.mock) {
      return this.getPublicImgPath('img/maps/path-point-fake.png')
    }

    if (marker.gpsOn) {
      return this.getPublicImgPath('img/maps/path-point-gps-on.png')
    }

    if (marker.gpsOff) {
      return this.getPublicImgPath('img/maps/path-point-gps-off.png')
    }

    return this.getPublicImgPath('img/maps/path-point-color-0.png')
  }

  getPathCountMarkerIcon (index: number) {
    let icon = new MarkerIcon()

    if (index === 0) {
      icon.url = this.getPublicImgPath('img/maps/path-point-start.png')
      icon.labelOrigin = new Point(60, 8)
      return icon
    }

    if (index === this.pathCountMarkers.length - 1) {
      icon.url = this.getPublicImgPath('img/maps/path-point-end.png')
      icon.labelOrigin = new Point(60, 10)
      return icon
    }

    icon.url = this.getPublicImgPath(`img/maps/path-point-color-${index % this.STROKE_COLORS.length}.png`)
    icon.labelOrigin = new Point(60, 8)
    return icon
  }

  /**
   * Show / Hide info window from the selected path marker
   */
  toggleInfoWindowPathMarker (marker: any, idx: any) {
    if (this.currentMidx === idx) {
      // check if its the same marker that was selected if yes toggle
      this.infoWinOpen = !this.infoWinOpen
    } else {
      this.infoWindowPos = marker.position
      this.infoContent = this.getInfoWindowContentPathMarker(marker)
      // if different marker set infowindow to open and reset current marker index
      this.infoWinOpen = true
      this.currentMidx = idx
    }
  }

  getPathMarkerLabel (index: number) {
    let label = new MarkerLabel()
    label.fontWeight = 'bold'

    if (index === 0) {
      label.text = 'Inicio de ruta'
      return label
    }

    if (index === this.pathMarkers.length - 1) {
      label.text = 'Final de ruta'
      return label
    }

    return null
  }

  getPathCountMarkerLabel (index: number) {
    let label = new MarkerLabel()
    label.fontWeight = 'bold'

    if (index === 0) {
      label.text = this.pathCountMarkers.length > 1 ? 'Inicio de ruta - Trayecto 1' : 'Inicio de ruta'
      return label
    }

    if (index === this.pathCountMarkers.length - 1) {
      label.text = 'Final de ruta'
      return label
    }

    label.text = `${this.pathLineEstimate[index] ? '*' : ''}Trayecto ${index + 1}`
    return label
  }

  getPublicImgPath (path: string) {
    return config.basePath + path
  }

  getStrokeColor (index: number) {
    return this.STROKE_COLORS[index % this.STROKE_COLORS.length]
  }

  calculateMean (arr: Array<number>) {
    const sum = arr.reduce((acc, val) => acc + val, 0)
    return sum / arr.length
  }

  calculateStdVar (arr: Array<number>) {
    const mean = this.calculateMean(arr)
    const squareDiffSum = arr.reduce((acc, val) => acc + (val - mean) ** 2, 0)
    const variance = squareDiffSum / (arr.length - 1)
    return Math.sqrt(variance)
  }
}
</script>

<style lang="scss">
.google-map {
  height: 100%;
}

.gm-style .gm-style-iw-c {
  border-radius: 3px;
}

.gm-style .bm-gm--iw {
  text-align: center;
  h1 {
    font-size: 0.85rem;
    border-bottom: 1px solid #ccc;
    padding-bottom: 5px;
  }
  h2 {
    font-size: 0.75rem;
    color: #919191;
  }
}
</style>
